'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { DialogProps } from '@radix-ui/react-dialog'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { useCreateFeedback } from '@/api/hooks/useCreateFeedback'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollable,
  DialogTitle,
} from '@/components/ui/dialog'

import { Field, FieldError, FieldLabel } from '../ui/field'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'

const schema = z.object({
  text: z.string().min(3),
  email: z.string().email().optional().or(z.literal('')),
})

type FeedbackInput = z.input<typeof schema>

type Props = DialogProps

export const FeedbackDialog = ({ ...dialogProps }: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: createFeedback } = useCreateFeedback()

  const form = useForm<FeedbackInput>({
    defaultValues: { text: '', email: '' },
    resolver: zodResolver(schema),
  })

  const handleClose = () => {
    form.reset()
    form.clearErrors()
    dialogProps.onOpenChange?.(false)
  }

  const handleSubmit = async (values: FeedbackInput) => {
    await createFeedback({
      requestBody: {
        text: values.text,
        email: values.email ? values.email : undefined,
        url: typeof window !== 'undefined' ? window.location.pathname : undefined,
      },
    })

    handleClose()
  }

  return (
    <Dialog {...dialogProps}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>{t('dialogs.feedback.title')}</DialogTitle>
          <DialogDescription>{t('dialogs.feedback.description')}</DialogDescription>
        </DialogHeader>

        <DialogScrollable>
          <FormProvider {...form}>
            <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
              <Field>
                <Textarea
                  {...form.register('text')}
                  className="min-h-[200px]"
                  placeholder={t('dialogs.feedback.textPlaceholder')}
                />
                {form.formState.errors.text && (
                  <FieldError>{form.formState.errors.text.message}</FieldError>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="feedback-email">
                  {t('dialogs.feedback.emailLabel')}
                </FieldLabel>
                <Input
                  id="feedback-email"
                  type="email"
                  placeholder={t('dialogs.feedback.emailPlaceholder')}
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <FieldError>{form.formState.errors.email.message}</FieldError>
                )}
              </Field>
            </form>
          </FormProvider>
        </DialogScrollable>

        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button onClick={handleClose} type="button" variant="outline">
              {t('global.cancel')}
            </Button>
            <Button
              disabled={form.formState.isSubmitting}
              onClick={form.handleSubmit(handleSubmit)}
              type="submit"
            >
              {t('dialogs.feedback.submitButton')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
